import { useCallback, useMemo } from 'react'
import { keyBy } from 'lodash-es'
import {
  COMMENTS_ELEMENT_LIST_TAB,
  DEFAULT_ELEMENT_LIST_TABS,
  ElementListItem,
  ElementListTab,
  IMAGE_CARDS_ELEMENT_LIST_TAB,
  TABLES_ELEMENT_LIST_TAB,
  useElementListItems,
} from '@editor/components/ElementListPane'
import useStructuralPlanningSelection from './useStructuralPlanningSelection'

const TABS: ElementListTab[] = [
  ...DEFAULT_ELEMENT_LIST_TABS,
  TABLES_ELEMENT_LIST_TAB,
  IMAGE_CARDS_ELEMENT_LIST_TAB,
  COMMENTS_ELEMENT_LIST_TAB,
]

/** The StructuralPlanning drawer dispatches rip/lintel forms on the singular mode. */
const toSelectionElementType = (
  elementType: ElementTypes,
): ElementTypes | 'rip' | 'lintel' | 'opening' => {
  if (elementType === 'rips') return 'rip'
  if (elementType === 'lintels') return 'lintel'
  return elementType
}

interface ElementListBinding {
  tabs: ElementListTab[]
  itemsByElementType: Record<string, ElementListItem[]>
  selectedIds: string[]
  onSelectionChange: (ids: string[]) => void
}

/**
 * Binds the shared element list pane to StructuralPlanning's selection — the same source the
 * scene writes and the drawer reads, so list ↔ scene ↔ drawer stay in sync.
 */
const useStructuralPlanningElementList = (): ElementListBinding => {
  const {
    params: { selectedElements },
    actions: { setElements, setMultiSelection, resetElements },
  } = useStructuralPlanningSelection()

  const itemsByElementType = useElementListItems(TABS)

  const itemById = useMemo(
    () => keyBy(Object.values(itemsByElementType).flat(), 'id'),
    [itemsByElementType],
  )

  const onSelectionChange = useCallback(
    (ids: string[]) => {
      if (ids.length === 0) {
        resetElements()
        return
      }
      if (ids.length === 1) {
        const elementType = itemById[ids[0]]?.elementType
        setElements(ids, elementType ? toSelectionElementType(elementType) : undefined)
        return
      }
      setMultiSelection(ids)
    },
    [itemById, resetElements, setElements, setMultiSelection],
  )

  return {
    tabs: TABS,
    itemsByElementType,
    selectedIds: selectedElements,
    onSelectionChange,
  }
}

export default useStructuralPlanningElementList
